import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Flame, Mail, Lock, User as UserIcon, Loader2, Gift, UserPlus, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { errMsg, authAPI } from '../services/api';

const GOALS = [
  'perdre du poids',
  'retrouver de l’énergie',
  'mieux manger',
  'reprendre une routine',
  'bouger davantage',
  'être accompagné(e)',
  'participer en équipe',
];

export default function Auth({ mode = 'login' }) {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const isLogin = mode === 'login';

  const [form, setForm] = useState({
    name: '', email: '', password: '', level: 'client', goal: 'mieux manger', consent_share: true,
    inviter_name: '', promo_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [promoStatus, setPromoStatus] = useState(null); // null | true | false
  const [verifying, setVerifying] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const verifyPromo = async () => {
    const code = form.promo_code.trim();
    if (!code) { setPromoStatus(null); return; }
    setVerifying(true);
    try {
      const res = await authAPI.verifyPromo(code);
      setPromoStatus(res.valid);
      if (res.valid) toast.success(res.message); else toast.error(res.message);
    } catch { setPromoStatus(false); }
    finally { setVerifying(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && form.promo_code.trim() && promoStatus === false) {
      toast.error('Corrige ou efface le code promo avant de continuer');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success(isLogin ? 'Bon retour parmi nous !' : 'Bienvenue dans le challenge !');
      navigate('/app');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-10">
      <Link to="/" className="flex flex-col items-center gap-3 mb-6" data-testid="auth-logo">
        <img src="/bfc21-logo.png" alt="Body Fit Challenge 21" className="w-20 h-20 rounded-full shadow-lg" />
      </Link>

      <Card className="w-full max-w-md p-6 sm:p-8 rounded-3xl border-red-100 shadow-lg">
        <h1 className="text-2xl font-extrabold tracking-tight text-red-950 dark:text-red-50 text-center">
          {isLogin ? 'Connexion' : 'Créer mon compte'}
        </h1>
        <p className="text-center text-muted-foreground text-sm mt-1 mb-6">
          {isLogin ? 'Reprends ton challenge où tu t’es arrêté(e)' : 'Rejoins le Body Fit Challenge 21'}
        </p>

        <Button
          type="button"
          variant="outline"
          onClick={googleLogin}
          data-testid="google-login-button"
          className="w-full rounded-xl font-semibold border-red-200 mb-5"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" />
          Continuer avec Google
        </Button>

        <div className="flex items-center gap-3 mb-5">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">ou avec ton email</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-1.5">
              <Label htmlFor="name">Nom complet</Label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input id="name" data-testid="register-name" required value={form.name} onChange={change('name')} placeholder="Ton nom" className="pl-10 rounded-xl" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input id="email" data-testid="auth-email" type="email" required value={form.email} onChange={change('email')} placeholder="ton@email.com" className="pl-10 rounded-xl" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Mot de passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input id="password" data-testid="auth-password" type="password" required value={form.password} onChange={change('password')} placeholder="••••••••" className="pl-10 rounded-xl" />
            </div>
          </div>

          {!isLogin && (
            <>
              <div className="space-y-1.5">
                <Label>Ton profil</Label>
                <div className="flex gap-2 p-1 bg-muted rounded-full">
                  {[['client', 'Client'], ['distributeur', 'Distributeur']].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`register-level-${v}`}
                      onClick={() => setForm((f) => ({ ...f, level: v }))}
                      className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
                        form.level === v ? 'bg-white dark:bg-red-900 text-red-700 dark:text-red-200 shadow-sm' : 'text-muted-foreground'
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="goal">Ton objectif principal</Label>
                <select
                  id="goal"
                  data-testid="register-goal"
                  value={form.goal}
                  onChange={change('goal')}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                >
                  {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inviter">Nom de la personne qui m'a invité(e) <span className="text-muted-foreground font-normal">(facultatif)</span></Label>
                <div className="relative">
                  <UserPlus className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input id="inviter" data-testid="register-inviter" value={form.inviter_name} onChange={change('inviter_name')} placeholder="Prénom Nom" className="pl-10 rounded-xl" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="promo">Code promo <span className="text-muted-foreground font-normal">(facultatif)</span></Label>
                <div className="relative">
                  <Gift className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="promo"
                    data-testid="register-promo"
                    value={form.promo_code}
                    onChange={(e) => { setPromoStatus(null); setForm((f) => ({ ...f, promo_code: e.target.value })); }}
                    onBlur={verifyPromo}
                    placeholder="Ex : bfc594"
                    className={`pl-10 pr-9 rounded-xl ${promoStatus === true ? 'border-green-500' : promoStatus === false ? 'border-red-500' : ''}`}
                  />
                  <span className="absolute right-3 top-3">
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : promoStatus === true ? <Check className="w-4 h-4 text-green-600" data-testid="promo-valid" /> : promoStatus === false ? <X className="w-4 h-4 text-red-500" data-testid="promo-invalid" /> : null}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground">Sans code ni invitant, le code <span className="font-semibold">bfc594</span> sera appliqué automatiquement.</p>
              </div>
            </>
          )}

          <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl py-6">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : isLogin ? 'Se connecter' : 'Créer mon compte'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {isLogin ? 'Pas encore de compte ? ' : 'Déjà inscrit(e) ? '}
          <Link to={isLogin ? '/register' : '/login'} className="text-red-600 font-semibold" data-testid="auth-switch-link">
            {isLogin ? 'Créer un compte' : 'Se connecter'}
          </Link>
        </p>
      </Card>
    </div>
  );
}
